import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  TIdentificationForm,
  TUpdateFormResponse,
  TUpdateFormRequest,
  TGetFormRequestAssetManagerRequest,
  TUpdateAssetManagerFormRequest,
  TCreateApplicationRequest,
  TFetchCreatedFormRequest,
  TIbanRquest,
  TIbanResponse,
  TCreateApplicationResponse,
  TSyncAdditionalInfoResponse,
} from '../types';
import api from '~/libs/api';
import { thunkErrorCreatedIdentification, thunkErrorHandler } from '../../utils';
import { TBaseRequest } from '~/store/types';
import { TDocument } from '~/modules/identification/forms/Documents/types';
import { checkRegenerateDocs } from './documentThunks';
import { RootState } from '~/store/store';

export const fetchIdentificationForm = createAsyncThunk<TIdentificationForm, TBaseRequest>(
  'identification/fetchForm',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/identification/screen-forms/get?app-id=${applicationId}`);
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchCreatedIdentificationForm = createAsyncThunk<
  TIdentificationForm,
  TFetchCreatedFormRequest
>('identification/fetchCreatedForm', async ({ applicationId, data: body }, { rejectWithValue }) => {
  try {
    const { data } = await api.post(
      `/identification/screen-forms/getting-started/application/${applicationId}`,
      body
    );
    return data;
  } catch (error) {
    return thunkErrorCreatedIdentification(error, rejectWithValue);
  }
});

export const createApplication = createAsyncThunk<
  TCreateApplicationResponse,
  TCreateApplicationRequest
>('identification/createApplication', async ({ identifyCode }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/identification/application', { identifyCode });

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const getIsAssetManagersChanged = createAsyncThunk<boolean, TBaseRequest>(
  'identification/getIsAssetManagersChanged',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/application/${applicationId}/asset-managers/has-data-changes`
      );
      const { anyAssetManagerDataChanged } = data;

      return anyAssetManagerDataChanged;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const managerCreateApplication = createAsyncThunk<
  TCreateApplicationResponse,
  TCreateApplicationRequest
>('identification/managerCreateApplication', async ({ identifyCode }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/identification/application/manager', { identifyCode });

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchCreatedIdentificationFormAssetManager = createAsyncThunk<
  TIdentificationForm,
  TGetFormRequestAssetManagerRequest
>(
  'identification/fetchCreatedFormAssetManager',
  async ({ managerId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}`
      );
      return data as TIdentificationForm;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const videoVerificationAssetManagers = createAsyncThunk<
  TIdentificationForm,
  TGetFormRequestAssetManagerRequest
>(
  'identification/videoVerificationAssetManagers',
  async ({ managerId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}/video-verification-initialize`,
        null
      );
      return data as TIdentificationForm;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateForm = createAsyncThunk<
  TSyncAdditionalInfoResponse | undefined,
  TUpdateFormRequest,
  { state: RootState }
>(
  'identification/updateForm',
  async ({ data, reportId, callbackFunc }, { rejectWithValue, dispatch }) => {
    try {
      await api.post('/identification/screen-forms/update', data);
      await callbackFunc?.();
      const docs = await dispatch(generateDocs({ data, reportId })).unwrap();
      const { applicationId, ...submittedForm } = data;

      await dispatch(
        checkRegenerateDocs({
          docs,
          appId: applicationId as number,
          reportId,
          data: submittedForm as Partial<TIdentificationForm>,
        })
      );
      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateFormAssetManager = createAsyncThunk<
  TUpdateFormResponse,
  TUpdateAssetManagerFormRequest
>(
  'identification/updateFormAssetManager',
  async ({ data, applicationId, managerId, reportId }, { rejectWithValue }) => {
    try {
      await api.post(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}`,
        data
      );
      const { data: response } = await api.post(
        `identification/reports/asset-managers/${managerId}/forms/${reportId}/applications/${data.applicationId}`,
        { data }
      );
      return response;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const requestAssetManagerToUpdateData = createAsyncThunk<
  boolean,
  TGetFormRequestAssetManagerRequest
>(
  'identification/requestAssetManagerToUpdateData',
  async ({ applicationId, managerId }, { rejectWithValue }) => {
    try {
      await api.post(
        `/identification/application/${applicationId}/asset-managers/${managerId}/request-data-update`,
        null
      );
      return true;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateFormChangeVideoVerification = createAsyncThunk<
  TIdentificationForm,
  TBaseRequest
>(
  'identification/updateFormChangeVideoVerification',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/identification/screen-forms/application/${applicationId}`);
      return data as TIdentificationForm;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const getAssetManagerDocs = createAsyncThunk<TDocument[], TBaseRequest>(
  'identification/getAssetManagerDocs',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `identification/application/${applicationId}/asset-managers/documents`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const generateDocs = createAsyncThunk<TUpdateFormResponse, TUpdateFormRequest>(
  'identification/generateDocs',
  async ({ reportId, data }, { rejectWithValue }) => {
    try {
      const { data: response } = await api.post(
        `identification/reports/forms/${reportId}/applications/${data.applicationId}/generate`,
        null
      );
      return response;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const infoByIban = createAsyncThunk<TIbanResponse, TIbanRquest>(
  'identification/infoByIban',
  async ({ data }) => {
    try {
      const { data: response } = await api.get(`/dictionary-service/bank/get-info-by-iban/${data}`);
      return response;
    } catch (error) {}
  }
);

export const updateFormFatcaAdditionalInfo = createAsyncThunk<TIdentificationForm, number>(
  'identification/updateFormFatcaAdditionalInfo',
  async (appId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/screen-forms/application/${appId}/fatca-additional-info/sync`,
        null
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
